import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import api from '../../services/api';

/**
 * Modal for an existing superuser to create another superuser (staff + is_superuser).
 */
const CreateSuperuserModal = ({ show, onHide, t }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleClose = () => {
    if (loading) return;
    setEmail('');
    setPassword('');
    setPasswordConfirm('');
    setError('');
    setSuccess('');
    onHide();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!email.trim()) {
      setError(t('adminSuperuser.emailRequired'));
      return;
    }
    if (password.length < 8) {
      setError(t('adminSuperuser.passwordTooShort'));
      return;
    }
    if (password !== passwordConfirm) {
      setError(t('adminSuperuser.passwordMismatch'));
      return;
    }
    setLoading(true);
    try {
      const res = await api.createSuperuser({
        email: email.trim().toLowerCase(),
        password,
        password_confirm: passwordConfirm,
      });
      const created = res?.data;
      setSuccess(
        t('adminSuperuser.created', {
          email: created?.email || email.trim(),
        }),
      );
      setEmail('');
      setPassword('');
      setPasswordConfirm('');
    } catch (err) {
      const et = err?.data?.errorText;
      if (typeof et === 'object') {
        const parts = [];
        Object.entries(et).forEach(([k, v]) => {
          const msg = Array.isArray(v) ? v.join(' ') : String(v);
          parts.push(`${k}: ${msg}`);
        });
        setError(parts.join(' ') || err.message);
      } else {
        setError(et || err.message || t('adminSuperuser.createFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{t('adminSuperuser.title')}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <p className="text-muted small">{t('adminSuperuser.intro')}</p>
          {error ? <Alert variant="danger">{error}</Alert> : null}
          {success ? <Alert variant="success">{success}</Alert> : null}
          <Form.Group className="mb-3">
            <Form.Label>{t('adminSuperuser.email')}</Form.Label>
            <Form.Control
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('adminSuperuser.password')}</Form.Label>
            <Form.Control
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label>{t('adminSuperuser.passwordConfirm')}</Form.Label>
            <Form.Control
              type="password"
              autoComplete="new-password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              disabled={loading}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" type="button" onClick={handleClose} disabled={loading}>
            {t('adminSuperuser.close')}
          </Button>
          <Button variant="warning" type="submit" disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" className="me-1" /> : null}
            {t('adminSuperuser.submit')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateSuperuserModal;
